"use client"
import type { EmblaCarouselType as CarouselApi } from "embla-carousel"
import AutoHeightPlugin from "embla-carousel-auto-height"
import AutoplayPlugin from "embla-carousel-autoplay"
import ClassNamesPlugin from "embla-carousel-class-names"
import useEmblaCarousel from "embla-carousel-react"
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useContextSelector } from "use-context-selector"
import { classNames } from "../../utils/classNames"
import { Block } from "../Block"
import { CarouselContext } from "./CarouselProvider"
import type { CarouselProps, CarouselSlideProps } from "./types"
import { getCarouselOptions } from "./utils"

function Carousel({
  animateHeight,
  autoplay,
  autoplayOptions,
  autoHeight,
  children,
  className,
  disableWheelScrolling,
  options,
  setApi,
  onScroll,
  ...props
}: CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    { inViewThreshold: 0.9, ...(options ? getCarouselOptions(options) : {}) },
    [
      ClassNamesPlugin(),
      autoplay
        ? AutoplayPlugin({
            rootNode: (carouselRoot: HTMLElement) => carouselRoot,
            ...autoplayOptions,
          })
        : null,
      autoHeight ? AutoHeightPlugin() : null,
      disableWheelScrolling ? null : WheelGesturesPlugin(),
    ].filter(Boolean),
  )

  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [navigationApi, setNavigationApi] = useState<CarouselApi | undefined>()

  const onSelect = useCallback(
    (api: CarouselApi) => {
      if (navigationApi) {
        navigationApi.scrollTo(api.selectedScrollSnap())
      }

      const apiIndex = api.selectedScrollSnap()
      const apiCanScrollPrev = api.canScrollPrev()
      const apiCanScrollNext = api.canScrollNext()

      setSelectedIndex(apiIndex)
      setCanScrollPrev(apiCanScrollPrev)
      setCanScrollNext(apiCanScrollNext)

      onScroll?.({
        index: apiIndex,
        canScrollPrev: apiCanScrollPrev,
        canScrollNext: apiCanScrollNext,
      })
    },
    [navigationApi, onScroll],
  )

  const scrollPrev = useCallback(() => {
    api?.plugins().autoplay?.stop()
    api?.scrollPrev()
  }, [api])

  const scrollNext = useCallback(() => {
    api?.plugins().autoplay?.stop()
    api?.scrollNext()
  }, [api])

  const scrollTo = useCallback(
    (...options: Parameters<CarouselApi["scrollTo"]>) => {
      api?.plugins().autoplay?.stop()
      api?.scrollTo(...options)
    },
    [api],
  )

  useEffect(() => {
    if (!(api && setApi)) {
      return
    }

    setApi(api)
  }, [api, setApi])

  useEffect(() => {
    if (!api) {
      return
    }

    onSelect(api)
    api.on("reInit", onSelect)
    api.on("select", onSelect)

    return () => {
      api?.off("select", onSelect)
      api?.off("reInit", onSelect)
    }
  }, [api, onSelect])

  const value = useMemo(
    () => ({
      carouselRef,
      api,
      autoHeight,
      animateHeight,
      options,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      navigationApi,
      setNavigationApi,
      scrollTo,
      selectedIndex,
    }),
    [
      carouselRef,
      api,
      autoHeight,
      animateHeight,
      options,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
      navigationApi,
      setNavigationApi,
      scrollTo,
      selectedIndex,
    ],
  )

  return (
    <CarouselContext.Provider value={value}>
      <Block
        aria-roledescription="carousel"
        className={classNames("group/carousel relative", className)}
        role="region"
        {...props}
      >
        {children}
      </Block>
    </CarouselContext.Provider>
  )
}

type CarouselContentProps = {
  className?: string
  allowOverflow?: boolean
} & ComponentProps<"div">

export function CarouselContent({
  className,
  allowOverflow,
  ...props
}: CarouselContentProps) {
  const [autoHeight, animateHeight, carouselRef, loop] = useContextSelector(
    CarouselContext,
    ctx => [
      ctx?.autoHeight,
      ctx?.animateHeight,
      ctx?.carouselRef,
      ctx?.options?.loop,
    ],
  )

  return (
    <div
      className={classNames(!allowOverflow && "overflow-hidden")}
      ref={carouselRef}
    >
      <div
        className={classNames(
          "flex",
          {
            // If the carousel is not set to loop, remove the margin on the last child
            "*:last:!mr-0": !loop,
            "items-start": autoHeight,
            "transition-height duration-200": animateHeight,
          },
          className,
        )}
        {...props}
      />
    </div>
  )
}

export function CarouselItem({
  className,
  style,
  ...props
}: CarouselSlideProps) {
  return (
    <Block
      aria-roledescription="slide"
      className={classNames(
        "min-w-0 shrink-0 grow-0 basis-full pl-4",
        className,
      )}
      style={{
        // TODO: Unsure if this is actually needed but can't hurt?
        // https://github.com/davidjerleke/embla-carousel/issues/890#issuecomment-2259080782
        transform: "translate3d(0, 0, 0)",
        ...style,
      }}
      {...props}
      role="group"
    />
  )
}

export { Carousel }

const useCarousel = useEmblaCarousel

export { useCarousel }
